import { WORKER_URL, CDN_URL } from '../../../js/config.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';
import { getDefaultStandingActionId } from '../../../js/utils/spriteFallbacks.js';
import { getNsfwEnabled } from '../../../js/utils/userPrefs.js';
import { getCurrentUserId, getSession } from '../../../js/utils/auth_utils.js';
import { filterScenariosForSfw, scenarioIsNsfw } from '../../../js/utils/nsfwTags.js';
import { mapScenarioRecord } from './scenarioMappers.js';
import { sessionImageCache } from './spritePreview.js';
import { filterBlockedScenarios } from '../../moderation/blockedContentStore.js';

const PRIVATE_SCENARIOS_URL = `${WORKER_URL}/private/scenarios`;

export const clearScenarioFetchCaches = () => {
  fetchCache.invalidate(PRIVATE_SCENARIOS_URL);
};

export const getScenarios = async (rankingsState) => {
  try {
    const currentLang = window.getCurrentLanguage?.() || 'en';
    const scenariosResponse = await fetchCache.fetchScenarios(currentLang);
    const list = Array.isArray(scenariosResponse) ? scenariosResponse : (scenariosResponse?.scenarios || []);
    const scenarios = filterBlockedScenarios(filterScenariosForSfw(list.map((s) => mapScenarioRecord(s))));
    rankingsState?.setScenarios(scenarios);
    return { data: scenarios };
  } catch (error) {
    console.error('Failed to get scenarios:', error);
    return { data: [] };
  }
};

export const getUserPrivateScenarios = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const session = await getSession();
    if (!session?.access_token) {
      throw new Error('Authentication required for private scenarios');
    }
    const result = await fetchCache.fetchJSON(PRIVATE_SCENARIOS_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (!result.success) {
      throw new Error('Failed to fetch private scenarios');
    }

    return filterScenariosForSfw(result.scenarios || []);
  } catch (error) {
    console.error('Error fetching private scenarios:', error);
    throw error;
  }
};

export const getScenarioDetails = async (scenarioId, rankingsState) => {
  if (!scenarioId) {
    return rankingsState?.scenarios || [];
  }

  if (isNaN(parseInt(scenarioId))) {
    return mapScenarioRecord({
      id: scenarioId,
      name: 'Unknown Scenario',
      description: 'No description available',
      chars: [],
      tags: []
    });
  }

  const scenario = rankingsState?.scenarios?.find(s =>
    s.id === scenarioId || s.id === parseInt(scenarioId)
  );

  if (scenario && (getNsfwEnabled() || !scenarioIsNsfw(scenario))) {
    return scenario;
  }

  const userId = getCurrentUserId();
  if (userId) {
    try {
      const privateScenarios = await getUserPrivateScenarios(userId);
      const privateScenario = privateScenarios.find(s => s.id === scenarioId || s.id === parseInt(scenarioId));

      if (privateScenario) {
        return mapScenarioRecord(privateScenario, { isPrivate: true });
      }
    } catch (error) {
      console.error('Error fetching private scenarios:', error);
    }
  }

  try {
    const currentLang = window.getCurrentLanguage?.() || 'en';
    const scenarios = await fetchCache.fetchScenarios(currentLang);
    const list = Array.isArray(scenarios) ? scenarios : (scenarios?.scenarios || []);
    const fetched = list.find(s => s.id === scenarioId || s.id === parseInt(scenarioId));

    if (fetched) {
      return mapScenarioRecord(fetched);
    }
  } catch (error) {
    console.error('Error fetching scenario details:', error);
  }

  return mapScenarioRecord({
    id: scenarioId,
    name: 'Unknown Scenario',
    description: 'No description available',
    chars: [],
    tags: []
  });
};

export const getScenarioSprite = async (
  characterId,
  scenarioId,
  fallbackImage = '/assets/images/kiss.webp',
  outfitId = null,
  rankingsState = null
) => {
  if (!characterId || isNaN(parseInt(characterId)) || !scenarioId || isNaN(parseInt(scenarioId))) {
    return fallbackImage;
  }

  const scenario = rankingsState?.scenarios?.find(s =>
    s.id === scenarioId || s.id === parseInt(scenarioId)
  );

  if (!scenario) return fallbackImage;

  const outfitIdStr = String(outfitId !== null && outfitId !== undefined ? outfitId : (scenario.outfits?.[0] || 0));
  const cacheKey = `${characterId}_${scenarioId}_${outfitIdStr}`;

  if (sessionImageCache.has(cacheKey)) return sessionImageCache.get(cacheKey);

  let gender = 'female';
  try {
    const character = rankingsState?.characters?.find(c =>
      c.id === characterId || c.id === String(characterId)
    );
    const isPrivate = character?.rank === 'Private';
    const c = await fetchCache.fetchCharacterDetails(characterId, isPrivate);
    if (c?.gender) gender = c.gender;
  } catch (error) {
    console.warn('getScenarioSprite: failed to get character details:', error);
  }

  try {
    const greetingResponse = await fetch(`${WORKER_URL}/greeting-sprite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Referer': (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? window.location.origin : 'https://kissandra.ai'
      },
      body: JSON.stringify({
        scenario_id: parseInt(scenarioId),
        character_ids: [parseInt(characterId)],
        outfit_ids: [parseInt(outfitIdStr)],
        sfw: !getNsfwEnabled()
      })
    });

    if (greetingResponse.ok) {
      const { actions } = await greetingResponse.json();
      if (actions?.[0] != null) {
        const { spriteUrl } = await fetchCache.fetchCharacterSprite(
          characterId, actions[0], parseInt(outfitIdStr), gender, false
        );
        if (spriteUrl) {
          sessionImageCache.set(cacheKey, spriteUrl);
          return spriteUrl;
        }
      }
    }
  } catch (greetingError) {
    console.warn('getScenarioSprite: greeting-sprite fetch failed:', greetingError);
  }

  try {
    const defaultActionId = getDefaultStandingActionId(gender);
    const { spriteUrl } = await fetchCache.fetchCharacterSprite(
      characterId, defaultActionId, parseInt(outfitIdStr), gender, false
    );
    if (spriteUrl) {
      sessionImageCache.set(cacheKey, spriteUrl);
      return spriteUrl;
    }
  } catch (error) {
    console.error('getScenarioSprite: fallback sprite fetch failed:', error);
  }

  return fallbackImage;
};

export const getBatchImageUrls = async (ids, dbService) => {
  try {
    if (!ids?.length) return {};

    const normalizedIds = ids.map(id => String(id).trim());
    const results = {};

    const uncachedIds = [];
    for (const id of normalizedIds) {
      if (sessionImageCache.has(id)) {
        results[id] = sessionImageCache.get(id);
      } else {
        uncachedIds.push(id);
      }
    }

    if (uncachedIds.length === 0) return results;

    const cachedBlobUrls = dbService?.getScenarioImageBlobs
      ? await dbService.getScenarioImageBlobs(uncachedIds)
      : {};

    for (const [id, url] of Object.entries(cachedBlobUrls)) {
      results[id] = url;
      sessionImageCache.set(id, url);
    }

    const remainingIds = uncachedIds.filter(id => !cachedBlobUrls[id]);
    if (remainingIds.length === 0) return results;

    const cacheKey = `batch_${[...normalizedIds].sort().join(',')}`;
    let batchOrCdn = sessionImageCache.get(cacheKey);
    if (!batchOrCdn) {
      batchOrCdn = remainingIds.reduce((map, id) => {
        map[id] = `${CDN_URL}/data/scenarios/images/${id}.webp`;
        return map;
      }, {});
    }

    remainingIds.forEach((id) => {
      const url = batchOrCdn[id] || `${CDN_URL}/data/scenarios/images/${id}.webp`;
      batchOrCdn[id] = url;
      sessionImageCache.set(id, url);
    });

    const merged = { ...batchOrCdn, ...results };
    sessionImageCache.set(cacheKey, merged);
    return merged;
  } catch (error) {
    console.error('Failed to get batch image URLs:', error);
    return {};
  }
};
